import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  api,
  authApi,
  twoFactorApi,
  isTwoFactorChallenge,
  type User,
  type RegisterData,
  type LoginData,
  type TwoFactorChallengeResponse,
  type ImpersonationResponse,
} from "./api";

interface TwoFactorChallenge {
  challengeToken: string;
  email: string;
  firstName: string | null;
}

interface LoginResult {
  success: boolean;
  error?: string;
  errorCode?: string;
  requires2FA?: boolean;
  challenge?: TwoFactorChallenge;
}

interface Complete2FAResult {
  success: boolean;
  error?: string;
  errorCode?: string;
  backupCodesRemaining?: number;
}

interface ImpersonationState {
  active: boolean;
  impersonatedBy: string;
  impersonatorEmail: string;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  impersonation: ImpersonationState | null;
  login: (data: LoginData) => Promise<LoginResult>;
  complete2FA: (
    challengeToken: string,
    code: string,
    trustDevice?: boolean
  ) => Promise<Complete2FAResult>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  startImpersonation: (data: ImpersonationResponse) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEY = "kvanten_access_token";
const REFRESH_TOKEN_KEY = "kvanten_refresh_token";
const TRUSTED_DEVICE_KEY = "kvanten_trusted_device";
const ADMIN_TOKEN_KEY = "kvanten_admin_access_token";
const ADMIN_REFRESH_TOKEN_KEY = "kvanten_admin_refresh_token";
const IMPERSONATION_KEY = "kvanten_impersonation";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        api.setAccessToken(token);
        try {
          const response = await authApi.me();
          if (response.success && response.data) {
            setUser(response.data.user);

            // Check for active impersonation
            const storedImpersonation = localStorage.getItem(IMPERSONATION_KEY);
            if (storedImpersonation) {
              try {
                setImpersonation(JSON.parse(storedImpersonation));
              } catch {
                localStorage.removeItem(IMPERSONATION_KEY);
              }
            }
          } else {
            // Token invalid, clear it
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(IMPERSONATION_KEY);
            api.setAccessToken(null);
          }
        } catch {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(IMPERSONATION_KEY);
          api.setAccessToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (data: LoginData): Promise<LoginResult> => {
    try {
      // Include trusted device token if available
      const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY);
      const loginData: LoginData = {
        ...data,
        trustedDeviceToken: trustedDeviceToken || undefined,
      };

      const response = await authApi.login(loginData);

      if (response.success && response.data) {
        // Check if 2FA is required
        if (isTwoFactorChallenge(response.data)) {
          const challenge = response.data as TwoFactorChallengeResponse;
          return {
            success: false,
            requires2FA: true,
            challenge: {
              challengeToken: challenge.challengeToken,
              email: challenge.user.email,
              firstName: challenge.user.firstName,
            },
          };
        }

        // Normal login - store tokens
        const { user: userData, tokens } = response.data;
        localStorage.setItem(TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        api.setAccessToken(tokens.accessToken);
        setUser(userData);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || "Inloggningen misslyckades",
        errorCode: response.error?.code,
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: "Kunde inte ansluta till servern. Kontrollera att API:et körs på port 3001.",
      };
    }
  }, []);

  const complete2FA = useCallback(
    async (
      challengeToken: string,
      code: string,
      trustDevice: boolean = false
    ): Promise<Complete2FAResult> => {
      try {
        const response = await twoFactorApi.verify({
          challengeToken,
          code,
          trustDevice,
        });

        if (response.success && response.data) {
          const { user: userData, tokens, trustedDeviceToken, backupCodesRemaining } = response.data;

          // Store tokens
          localStorage.setItem(TOKEN_KEY, tokens.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
          api.setAccessToken(tokens.accessToken);

          // Store trusted device token if provided
          if (trustedDeviceToken) {
            localStorage.setItem(TRUSTED_DEVICE_KEY, trustedDeviceToken);
          }

          setUser(userData);

          return {
            success: true,
            backupCodesRemaining,
          };
        }

        return {
          success: false,
          error: response.error?.message || "Ogiltig kod",
          errorCode: response.error?.code,
        };
      } catch (error) {
        console.error("2FA verification error:", error);
        return {
          success: false,
          error: "Kunde inte verifiera koden. Försök igen.",
        };
      }
    },
    []
  );

  const register = useCallback(async (data: RegisterData) => {
    try {
      const response = await authApi.register(data);

      if (response.success && response.data) {
        const { user: userData, tokens } = response.data;
        localStorage.setItem(TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        api.setAccessToken(tokens.accessToken);
        setUser(userData);
        return { success: true };
      }

      return {
        success: false,
        error: response.error?.message || "Registreringen misslyckades",
      };
    } catch (error) {
      console.error("Register error:", error);
      return {
        success: false,
        error: "Kunde inte ansluta till servern. Kontrollera att API:et körs på port 3001.",
      };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.me();
      if (response.success && response.data) {
        setUser(response.data.user);
      }
    } catch {
      // Silently fail — user state unchanged
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      await authApi.logout(refreshToken || undefined);
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    // Note: We keep the trusted device token so 2FA is skipped on next login
    api.setAccessToken(null);
    setUser(null);
    setImpersonation(null);
  }, []);

  const startImpersonation = useCallback((data: ImpersonationResponse) => {
    // Save current admin tokens before switching
    const currentToken = localStorage.getItem(TOKEN_KEY);
    const currentRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (currentToken) {
      localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
    }
    if (currentRefresh) {
      localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, currentRefresh);
    }

    // Switch to impersonated user tokens
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    api.setAccessToken(data.accessToken);

    // Store impersonation state
    const impersonationState: ImpersonationState = {
      active: true,
      impersonatedBy: data.impersonation.impersonatedBy,
      impersonatorEmail: data.impersonation.impersonatorEmail,
    };
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonationState));
    setImpersonation(impersonationState);

    // Update user to the impersonated user
    setUser({
      id: data.user.id,
      email: data.user.email,
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      role: data.user.role,
    });
  }, []);

  const stopImpersonation = useCallback(() => {
    // Restore admin tokens
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);

    if (adminToken) {
      localStorage.setItem(TOKEN_KEY, adminToken);
      api.setAccessToken(adminToken);
    }
    if (adminRefresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, adminRefresh);
    }

    // Clear impersonation data
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonation(null);

    // Refresh user to get admin user back
    if (adminToken) {
      authApi.me().then((response) => {
        if (response.success && response.data) {
          setUser(response.data.user);
        }
      });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        impersonation,
        login,
        complete2FA,
        register,
        logout,
        refreshUser,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
